import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { ChatSheet } from './ChatSheet';

interface ChatContextType {
  isOpen: boolean;
  open: () => void;
  close: () => void;
}

const ChatContext = createContext<ChatContextType | null>(null);

export function useGlobalChat() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useGlobalChat must be used within a ChatProvider');
  }
  return context;
}

interface ChatProviderProps {
  children: ReactNode;
}

export function ChatProvider({ children }: ChatProviderProps) {
  const navigate = useNavigate();
  const routerState = useRouterState();
  
  // Chat state
  const [isOpen, setIsOpen] = useState(false);

  // Read initial state from URL on mount and router changes
  const getStateFromUrl = useCallback(() => {
    const search = routerState.location.search as Record<string, any>;

    return {
      isOpen: search.chatOpen !== undefined,
    };
  }, [routerState.location.search]);

  // Initialize and watch state from URL
  useEffect(() => {
    const urlState = getStateFromUrl();
    setIsOpen(urlState.isOpen);
  }, [getStateFromUrl]);

  // Update URL when chat state changes
  const updateUrl = useCallback((newIsOpen: boolean) => {
    const currentSearch = routerState.location.search as Record<string, any>;
    
    const newSearch = { ...currentSearch };
    if (newIsOpen) {
      newSearch.chatOpen = 'true';
    } else {
      delete newSearch.chatOpen;
    }

    navigate({
      to: window.location.pathname,
      search: newSearch,
      replace: true,
    });
  }, [navigate, routerState.location.search]);

  const open = useCallback(() => {
    setIsOpen(true);
    updateUrl(true);
  }, [updateUrl]);

  const close = useCallback(() => {
    setIsOpen(false);
    updateUrl(false);
  }, [updateUrl]);

  // Listen for popstate (browser back/forward) to sync state
  useEffect(() => {
    const handlePopState = () => {
      const urlState = getStateFromUrl();
      setIsOpen(urlState.isOpen);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [getStateFromUrl]);


  const contextValue: ChatContextType = {
    isOpen,
    open,
    close,
  };

  return (
    <ChatContext.Provider value={contextValue}>
      {children}
      {/* Only render chat if user is authenticated or we're allowing unauthenticated access */}
      <ChatSheet
        isOpen={isOpen}
        onClose={close}
      />
    </ChatContext.Provider>
  );
} 